import { IGetListService } from "../../../../abstractions/services/actions/IGetListService";
import { serviceProvider } from "../../../../comun/provider/ServiceProvider";
import { createCachedFactory } from "../../../../utils/factoryUtils";
import { toDateOnly } from "../../../../utils/fechaUtils";
import { CompraAnimalesResponse } from "../response/CompraAnimalesResponse";
import { RotacionPesajeVeterinariaResponse } from "../response/RotacionPesajeVeterinariaResponse";
import { VentaAnimalesResponse } from "../response/VentaAnimalesResponse";
import { fx_createCompraAnimalesGrpcService } from "./CompraAnimalesGrpcService";
import { fx_createRotacionPesajeVeterinariaGrpcService } from "./RotacionPesajeVeterinariaGrpcService";
import { fx_createVentaAnimalesGrpcService } from "./VentaAnimalesGrpcService";

/** Item del histórico de un lote: una fila por registro fuente (compra, venta o
 *  evento RPV), discriminada por `tipo`. Conserva la response original para
 *  abrir su detalle. */
export type HistoricoLoteItem =
  | {
      /** Id del registro fuente. */
      id: string;
      /** Fecha del movimiento (ISO). */
      fecha: string;
      tipo: "compra";
      /** Compra que ingresó animales a este lote. */
      compra: CompraAnimalesResponse;
    }
  | {
      id: string;
      fecha: string;
      tipo: "venta";
      /** Venta que sacó animales de este lote. */
      venta: VentaAnimalesResponse;
    }
  | {
      id: string;
      fecha: string;
      tipo: "rpv";
      /** Evento de Pesaje / Rotación / Veterinaria sobre este lote. */
      evento: RotacionPesajeVeterinariaResponse;
      /** `true` si la rotación INGRESÓ animales a este lote (era el destino). */
      esIngresoPorRotacion: boolean;
    };

/** Histórico de movimientos de un lote, armado en el cliente (sin API): cruza
 *  las listas locales de compras (lote de entrada), ventas (lote de salida) y
 *  eventos RPV (origen o destino) de la finca activa, ordenado por fecha
 *  descendente. Los list services fuente ya se invalidan en cada save, así que
 *  el histórico siempre refleja el último estado sincronizado. */
class HistoricoLoteGetListService implements IGetListService<HistoricoLoteItem> {
  /** @param _loteId Lote cuyo histórico se arma. */
  constructor(private readonly _loteId: string) {}

  /** Junta, filtra por lote y ordena los movimientos. */
  async getListAsync(): Promise<HistoricoLoteItem[]> {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    const [compras, ventas, eventos] = await Promise.all([
      fx_createCompraAnimalesGrpcService(finca.id).getListAsync(),
      fx_createVentaAnimalesGrpcService(finca.id).getListAsync(),
      fx_createRotacionPesajeVeterinariaGrpcService(
        finca.id,
      ).getListAsync(),
    ]);

    const items: HistoricoLoteItem[] = [
      ...compras
        .filter((compra) => compra.loteEntrada.id === this._loteId)
        .map((compra) => ({
          id: compra.id,
          fecha: compra.fechaCompra,
          tipo: "compra" as const,
          compra: compra,
        })),
      ...ventas
        .filter((venta) => venta.loteSalida.id === this._loteId)
        .map((venta) => ({
          id: venta.id,
          fecha: venta.fechaVenta,
          tipo: "venta" as const,
          venta: venta,
        })),
      ...eventos
        .filter(
          (evento) =>
            evento.loteOrigenId === this._loteId ||
            evento.loteDestinoId === this._loteId,
        )
        .map((evento) => ({
          id: evento.id,
          fecha: evento.fecha,
          tipo: "rpv" as const,
          evento: evento,
          esIngresoPorRotacion:
            evento.isRotacion && evento.loteDestinoId === this._loteId,
        })),
    ];

    return items.sort((a, b) =>
      toDateOnly(b.fecha).localeCompare(toDateOnly(a.fecha)),
    );
  }

  /** No-op: las fuentes (compras/ventas/RPV) ya se invalidan en cada save. */
  clearCache(): void {}
}

/** Factory cacheada: retorna una única instancia por loteId. */
export const fx_createHistoricoLoteGetListService = createCachedFactory(
  (loteId) => new HistoricoLoteGetListService(loteId),
);
